import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/layout/page-header';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * The chrome the three setup lists share.
 *
 * All three are the same page with different fields: a flat list nobody pages
 * or filters, a create button, and an editor that also deletes. Writing that
 * shell once is what keeps the three from drifting into three slightly
 * different ideas of the same screen, which is what happened before.
 */

export function SetupPage({
  title,
  description,
  onCreate,
  onReload,
  reloading,
  isPending,
  isError,
  errorMessage,
  isEmpty,
  emptyMessage,
  children,
}: {
  title: string;
  description: string;
  onCreate: () => void;
  onReload: () => void;
  reloading: boolean;
  isPending: boolean;
  isError: boolean;
  errorMessage: string;
  isEmpty: boolean;
  emptyMessage: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        description={description}
        actions={
          <>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('bookingSetup.reload')}
              disabled={reloading}
              onClick={onReload}
            >
              <RefreshCw className={reloading ? 'animate-spin' : ''} />
            </Button>
            <Button size="sm" onClick={onCreate}>
              <Plus />
              <span className="hidden sm:inline">
                {t('bookingSetup.createNew')}
              </span>
            </Button>
          </>
        }
      />

      {isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{errorMessage}</p>
            <Button variant="outline" size="sm" onClick={onReload}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <Skeleton className="size-10 shrink-0 rounded-lg" />
              <Skeleton className="h-4 w-48" />
            </div>
          ))}
        </div>
      )}

      {isEmpty && (
        <div className="relative rounded-xl">
          <BusyOverlay show={reloading} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {emptyMessage}
          </p>
        </div>
      )}

      {children}
    </div>
  );
}

/** One labelled control in an editor. */
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

/**
 * The editor drawer, with delete in the header rather than loose above the
 * fields — it is the one destructive thing here and it belongs beside the
 * title, not in the flow a person is filling in.
 */
export function SetupEditor({
  title,
  open,
  onOpenChange,
  onSave,
  saving,
  canSave,
  isEdit,
  onDelete,
  deleting,
  deletePrompt,
  children,
}: {
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  saving: boolean;
  canSave: boolean;
  isEdit: boolean;
  onDelete?: () => void;
  deleting?: boolean;
  deletePrompt?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md"
      >
        <SheetHeader className="flex-row items-center justify-between gap-3 border-b pr-12">
          <SheetTitle>{title}</SheetTitle>
          {isEdit && onDelete && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  disabled={saving || deleting}
                >
                  <Trash2 className="size-3.5" />
                  {t('bookingSetup.delete')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('bookingSetup.delete')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {deletePrompt}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={onDelete}
                  >
                    {t('common.yes')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </SheetHeader>

        <div className="space-y-4 p-4">
          {children}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              disabled={saving}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button disabled={!canSave || saving} onClick={onSave}>
              {saving && <Loader2 className="animate-spin" />}
              {isEdit ? t('common.save') : t('bookingSetup.create')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
